<template>
    <md-table class="carrier-manifests-table">
        <md-table-row slot="md-table-head">
            <md-table-head>Shipping Date</md-table-head>
            <md-table-head>Carrier</md-table-head>
            <md-table-head>No. of Packages</md-table-head>
            <md-table-head>Actions</md-table-head>
        </md-table-row>
        <template v-if="manifests.length === 0">
            <md-table-row>
                <md-table-cell colspan="4" class="text-center">
                    No manifests available.
                </md-table-cell>
            </md-table-row>
        </template>
        <template v-for="manifest in manifests" v-else>
            <md-table-row
                @click="activateManifest(manifest)"
                :class="{ 'active-manifest': activeManifestId === manifest.carrierJobManifestId }"
                :key="manifest.carrierJobManifestId"
            >
                <md-table-cell>{{ manifest.shippingDate ? formatDate(manifest.shippingDate) : '' }}</md-table-cell>
                <md-table-cell class="carrier-cell">
                    <img :src="manifest.carrierLogoUrl" alt="logo" class="carrier-logo" />
                    <span>{{ manifest.carrierName }}</span>
                </md-table-cell>
                <md-table-cell>{{ manifest.packagesCount }}</md-table-cell>
                <md-table-cell>
                    <div class="action-group" v-if="manifest.hasDownloadableDocument">
                        <md-button
                            class="md-accent md-just-icon md-round"
                            :href="manifest.downloadUrl"
                            target="_blank"
                            title="Download manifest"
                            v-if="manifest.downloadUrl"
                        >
                            <md-icon>downloading</md-icon>
                        </md-button>
                        <md-button
                            class="md-accent md-just-icon md-round"
                            @click.stop="$emit('download-labels', manifest)"
                            title="Download all labels"
                        >
                            <md-icon>file_download</md-icon>
                        </md-button>
                    </div>
                </md-table-cell>
            </md-table-row>

            <md-table-row
                v-if="activeManifestId === manifest.carrierJobManifestId"
                :key="`nested-row-${manifest.carrierJobManifestId}`"
            >
                <md-table-cell colspan="4" class="nested-table-cell">
                    <skeleton-table v-if="isOrdersLoading"></skeleton-table>
                    <simplified-order-table
                        v-else
                        :orders="fetchedOrders"
                        :is-read-only-user="isReadOnlyUser"
                        :column-dropdown-options="columnDropdownOptions"
                        :selected-column="selectedColumn"
                        @column-selected="handleSelectedColumn"
                    ></simplified-order-table>
                </md-table-cell>
            </md-table-row>
        </template>
    </md-table>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { handleRequests, showErrorMessage } from '@/helpers';
import SimplifiedOrderTable from './SimplifiedOrderTable';
import SkeletonTable from './SkeletonTable';

export default {
    name: 'CarrierManifestsTable',
    components: { SimplifiedOrderTable, SkeletonTable },
    props: {
        manifests: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            activeManifestId: null,
            fetchedOrders: [],
            isOrdersLoading: false,
            columnDropdownOptions: [
                { key: 'order-number', name: 'Order #' },
                { key: 'source-reference', name: 'Source Ref' }
            ],
            selectedColumn: { key: 'order-number', name: 'Order #' }
        };
    },
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser'
        })
    },
    methods: {
        formatDate(date) {
            if (!date) 
                return '';
            const momentDate = moment(date);
            return momentDate.format('MMMM D, YYYY h:mm A');
        },
        async activateManifest(manifest) {
            // If clicking the already active manifest, deactivate it
            if (this.activeManifestId === manifest.carrierJobManifestId) {
                this.activeManifestId = null;
                this.fetchedOrders = [];
                return;
            }

            // Activate the new manifest and fetch orders
            this.activeManifestId = manifest.carrierJobManifestId;
            this.fetchedOrders = []; // Clear previous orders
            this.isOrdersLoading = true;

            try {
                const endpoint = `/api/carrier-jobs/manifests/${manifest.carrierJobManifestId}/orders`;
                const response = await handleRequests(endpoint);

                this.fetchedOrders = response.data.orders;
            } catch (error) {
                const message = 'Error fetching orders for manifest';
                showErrorMessage(this, message, error);
                this.fetchedOrders = []; // Clear orders on error
            } finally {
                this.isOrdersLoading = false;
            }
        },
        handleSelectedColumn(item) {
            this.selectedColumn = item;
        }
    }
};
</script>

<style scoped lang="scss">
.carrier-logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
    border-radius: 4px;
    background: #fff;
    border: 1px solid #eee;
    margin-right: 8px;
}

.carrier-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.active-manifest {
    background-color: #f5f5f5; /* Highlight color for active row */
}

.nested-table-cell {
    padding: 0 !important; /* Remove padding from the cell containing the nested table */
    background-color: #f5f5f5; /* Match background of active row */
}

.action-group {
    display: flex;
    gap: 8px;
}
</style>
